
import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Calendar, Clock } from "lucide-react";
import { type TimeSlot } from "./TimeSlotManager";

type DaySchedule = {
  day: number;
  slots: TimeSlot[];
};

type AvailabilitySummaryProps = {
  weeklySchedule: DaySchedule[];
  selectedDay: number | null;
  dayNames: string[];
  loading: boolean;
};

const AvailabilitySummary = ({
  weeklySchedule,
  selectedDay,
  dayNames,
  loading
}: AvailabilitySummaryProps) => {
  return (
    <Card className="lg:col-span-2 bg-gradient-to-b from-[#1E293B] to-[#1A212E] border-[#2A3749] shadow-xl backdrop-blur-sm">
      <CardHeader className="pb-4">
        <CardTitle className="text-white text-xl">Minha Disponibilidade</CardTitle>
        <CardDescription className="text-gray-400">Resumo de todos os dias configurados</CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="animate-pulse flex flex-col items-center">
              <div className="h-6 w-16 bg-[#242C37] rounded mb-4"></div>
              <div className="h-20 w-full bg-[#242C37] rounded-lg"></div>
              <div className="h-20 w-full bg-[#242C37] rounded-lg mt-3"></div>
            </div>
          </div>
        ) : weeklySchedule.length > 0 ? (
          <div className="space-y-3 max-h-[480px] overflow-y-auto pr-1 custom-scrollbar">
            {weeklySchedule
              .sort((a, b) => a.day - b.day)
              .map((daySchedule) => (
                <div 
                  key={daySchedule.day} 
                  className={`
                    p-4 rounded-lg border transition-all duration-200 
                    ${selectedDay === daySchedule.day 
                      ? 'bg-[#242C37]/90 border-[#00B6F0]/40 shadow-md' 
                      : 'bg-[#161D29]/80 border-[#2A3749] hover:bg-[#1A212E]'
                    }
                  `}
                >
                  <p className="text-white font-medium mb-2 flex items-center gap-2">
                    <span
                      className="h-3 w-3 rounded-full bg-[#00B6F0]"
                      aria-hidden="true"
                    ></span>
                    {dayNames[daySchedule.day]}
                  </p>
                  <div className="space-y-1 pl-5">
                    {daySchedule.slots
                      .sort((a, b) => a.start.localeCompare(b.start))
                      .map((slot, slotIndex) => (
                        <p key={slotIndex} className="text-[#00B6F0] text-sm flex items-center gap-2">
                          <Clock className="h-3 w-3 text-gray-400" />
                          <span>
                            {slot.start} às {slot.end}
                          </span>
                        </p>
                      ))
                    }
                  </div>
                </div>
              ))
            }
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 text-center space-y-3 bg-[#161D29]/50 rounded-xl border border-dashed border-[#2A3749]">
            <Calendar className="h-10 w-10 text-gray-500 mb-2" />
            <p className="text-gray-400">Nenhuma disponibilidade configurada</p>
            <p className="text-xs text-gray-500 max-w-xs">
              Configure seus horários selecionando os dias da semana e adicionando intervalos de disponibilidade
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AvailabilitySummary;
